"use client";

import React, { forwardRef, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import CustomImage from "../image";
import { LayoutContext } from "../layout-context-container";
import { lockScroll, unlockScroll } from "@/util/scrollLock";

function Modal({
  showCloseButton = true,
  variant = undefined,
  onOpen = undefined,
  onClose = undefined,
  ariaLabelledby = undefined, // provide id
  ariaDescribedby = undefined,  // provide id
  customClass = "",
  children
}, forwardRef) {
  const containerRef = useRef(null);
  const scrollLockId = useId();
  const { setInterstitialStateOpen } = useContext(LayoutContext);
  const [stateOpen, setStateOpen] = useState(false);
  const [stateOpenDelayed, setStateOpenDelayed] = useState(false);
  const [init, setInt] = useState(false);
  const [refocus, setRefocus] = useState(false);
  const [disableClose, setDisableClose] = useState(false);

  const containerClass = useMemo(() => {
    var className = 'modal';
    if (stateOpenDelayed) {
      className = `${className} opacity-100`;
    } else  {
      className = `${className} opacity-0`;
    }
    if (customClass) {
      className = `${className} ${customClass}`;
    }
    if (variant) {
      let variantArray;
      if (!Array.isArray(variant)) {
        variantArray = [variant];
      } else {
        variantArray = variant;
      }
      variantArray.forEach((v) => {
        className = `${className} modal--${v}`;
      });
    }
    return className;
  }, [customClass, stateOpenDelayed, variant]);

  useEffect(() => {
    if (stateOpen && onOpen) {
      onOpen(true);
    } else if (init && !stateOpen && onClose) {
      onClose(false);
    } else if (!init) {
      setInt(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOpen]);

  useEffect(() => {
    if (stateOpen) {
      setTimeout(() => setStateOpenDelayed(true), 150);
      lockScroll(scrollLockId);
    } else {
      setStateOpenDelayed(false);
      unlockScroll(scrollLockId);
    }

    return () => unlockScroll(scrollLockId);
  }, [stateOpen]);

  useEffect(() => {
    const modal = containerRef.current;
    let firstFocusableElement;
    let lastFocusableElement;
    
    if (modal) {
      const focusableElements = modal?.querySelectorAll('input, a[href], button, [tabindex]:not([tabindex="-1"])');
      firstFocusableElement = focusableElements[0];
      lastFocusableElement = focusableElements[focusableElements.length - 1];
    }

    function handeKeyDown(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement?.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement?.focus();
          }
        }
      }
    }

    if (stateOpenDelayed || refocus) {
      // containerRef.current?.focus();

      setTimeout(() => firstFocusableElement?.focus(), 333);
      modal?.addEventListener('keydown', handeKeyDown);

      setRefocus(false);
    }
    
    return () => !stateOpenDelayed && modal?.addEventListener('keydown', handeKeyDown);

  }, [stateOpenDelayed, refocus]);

  useImperativeHandle(
    forwardRef,
    () => ({
      open() {
        setStateOpen(true);
      },
      close() {
        setStateOpen(false);
      },
      get state() {
        return stateOpen;
      },
      refocus() {
        setRefocus(true);
      },
      setDisableClose,
    }),
    [stateOpen]
  );

  return (
    <>
      {/* modal */}
      {stateOpen && (
        <div
          ref={containerRef}
          className={containerClass}
          onClick={(e) => {
            if (e.target === containerRef.current && !disableClose) {
              e.stopPropagation();
              setStateOpen(false);
              setInterstitialStateOpen(false);
            }
          }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
        >
          {/* modal content */}
          <div className={`modal__content container--px-20${stateOpenDelayed ? " opacity-100" : " opacity-0"}`}>
            <div className="modal__content__body">
              {children}
              {showCloseButton ? (
                <button 
                  className="modal__close"
                  onClick={() => {
                    if (!disableClose) {
                      setStateOpen(false);
                      setInterstitialStateOpen(false);
                    }
                  }}
                  data-gtm-event-category="Action"
                  data-gtm-event-action="Click"
                  data-gtm-event-label="Close Modal"
                >
                  <CustomImage
                    alt=""
                    src="/images/icons/Close-Icon.svg"
                    width={30}
                    height={30}
                    className="modal__close-icon"
                  />
                  Close
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default forwardRef(Modal);